import type {
  Product,
  ProductVariant,
  ProductQuestion,
  ListingReport,
  Vendor,
  ServiceProvider,
  Classified,
  ClassifiedStatus,
} from '@shopgnd/shared';
import {
  DIRECTORY_CATEGORY_TO_BUSINESS_TYPE,
  defaultModesForDirectoryCategory,
  inferBusinessModesForSignup,
  normalizeDirectoryCategoryCustomLabel,
  parseDeliveryParishRates,
  primaryModeForBusinessType,
  primaryModeForModes,
  resolveDirectoryClassification,
  parseProductVariantConfig,
  serializeDeliveryParishRates,
  parseBookingNotifyChannels,
  type ProductVariantConfig,
  type SignupBusinessGoals,
} from '@shopgnd/shared';
import { PENDING_CATEGORY_ID, isPublishedProduct, isPurchasableProduct, productStockStatus } from '@/lib/productListing';
import type { StockStatusFilter } from '@/lib/productListing';
import { buildListingSlug } from '@/lib/utils/slug';
import { createCategoryRequest } from '@/api/categoryRequests';
import { assignHubRole } from '@/api/roles';
import { refreshAuthUser } from '@/lib/auth';
import { commissionBpsForPlan } from '@/lib/platformFees';
import { vendorHasPropertyListingAccess } from '@/lib/propertyListingApproval';
import { isRealEstateVendor } from '@/lib/realEstateVendor';
import { getPlatformSettings } from '@/api/platformSettings';
import { notifyCatalogRefresh } from '@/lib/catalog-refresh';
import { trialEndsAtForSignup } from '@/lib/subscriptionLaunch';
import type { PlanId } from '@/lib/vendorPlans';
import { vendorAtListingLimit, vendorHasFeature, vendorListingLimit } from '@/lib/vendorPlanGates';
import { getAuthedDataClient, getCatalogClient } from '@/lib/amplify-client';
import { recordSlugRedirect } from '@/api/seoRedirects';
import {
  createProductViaLambda,
  isProductListingLambdaUnreachableError,
  updateProductViaLambda,
} from '@/api/productListing';
import { callerIsShopAdmin } from '@/lib/auth';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  normalizeProductCategoryIds,
  productMatchesCategoryFilter,
} from '@/lib/productCategories';
import { buildProVendorShell, parseProBusinessSlug, proBusinessSlug, proPublicName } from '@/lib/proVendorShell';
import { isVendorUuid, slugifyVendorName } from '@/lib/vendorUrl';
import { normalizeStoredMediaUrl } from '@/lib/mediaUrl';
import { asStringArray } from '@/lib/coerce';

/** Typed models ship with `amplify_outputs.json`; loosen here so the web package builds before sandbox. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyDataClient = any;

const nowIso = () => new Date().toISOString();

function normalizeImageList(images: string[]): string[] {
  return images.map(normalizeStoredMediaUrl);
}

function normalizeVariants(variants: ProductVariant[] | undefined): ProductVariant[] | undefined {
  if (!variants || variants.length === 0) return undefined;
  return variants.map((v) => ({
    ...v,
    label: String(v.label ?? '').trim(),
    description: v.description?.trim() ? String(v.description).trim() : undefined,
    attributes: v.attributes && typeof v.attributes === 'object' ? { ...v.attributes } : {},
    priceDeltaCents: Number(v.priceDeltaCents ?? 0),
    stock: Number(v.stock ?? 0),
  }));
}

function serializeVariants(variants: ProductVariant[] | undefined): string | undefined {
  const normalized = normalizeVariants(variants);
  if (!normalized || normalized.length === 0) return undefined;
  return JSON.stringify(normalized);
}

function deserializeVariants(raw: unknown): ProductVariant[] | undefined {
  if (!raw) return undefined;
  if (Array.isArray(raw)) return normalizeVariants(raw as ProductVariant[]);
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return normalizeVariants(parsed as ProductVariant[]);
    } catch {
      return undefined;
    }
  }
  return undefined;
}

function isPairsWithSchemaError(message: string): boolean {
  return message.toLowerCase().includes('pairswith');
}

function isCategoryIdsSchemaError(message: string): boolean {
  const lower = message.toLowerCase();
  return lower.includes('categoryids') && lower.includes('createproductinput');
}

function isVendorDefaultsSchemaError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('updatevendorinput') &&
    (lower.includes('pickuplatitude') ||
      lower.includes('defaultproductpickup') ||
      lower.includes('defaultproductlocaldelivery') ||
      lower.includes('defaultproductdelivery') ||
      lower.includes('onlineonly'))
  );
}

function isProductInputSchemaError(message: string): boolean {
  return (
    message.includes("field that is not defined for input object type 'UpdateProductInput'") ||
    message.includes("field that is not defined for input object type 'CreateProductInput'")
  );
}

/** GraphQL AppSync often names the rejected field in the error text. */
function unknownProductInputField(message: string): string | undefined {
  const patterns = [
    /field ['"]?(\w+)['"]? is not defined/i,
    /Field ['"]?(\w+)['"]? is not defined/i,
    /input object type ['"]?\w+ProductInput['"]?.*\b(\w+)\b/i,
  ];
  for (const re of patterns) {
    const m = message.match(re);
    if (m?.[1] && m[1] !== 'input' && m[1] !== 'type') return m[1];
  }
  return undefined;
}

const PRODUCT_EXTENSION_FIELDS = [
  'variantConfig',
  'variantMultiSelect',
  'deliveryPriceCents',
  'deliveryParishRates',
] as const;

function stripProductExtensionFields(payload: Record<string, unknown>): Record<string, unknown> {
  const next = { ...payload };
  for (const key of PRODUCT_EXTENSION_FIELDS) {
    delete next[key];
  }
  return next;
}

function mapVariantSchemaError(message: string): string {
  if (message === 'Internal Server Error' || message.includes('Internal Server Error')) {
    return 'Product save failed on the API. Saving now routes through the listing service — redeploy the Amplify backend if this persists after a hard refresh.';
  }
  if (isProductInputSchemaError(message)) {
    const field = unknownProductInputField(message);
    if (field === 'variants') {
      return 'Product variants need a backend deploy before saving. Run pipeline-deploy, then try again.';
    }
    return 'Some product options need a backend deploy (multi-select variants, delivery fees, or variety picker). Deploy the latest main branch in Amplify, then save again.';
  }
  if (message.includes("Variable 'variants' has an invalid value")) {
    return 'Invalid variant payload format. Please re-open the product and try saving again.';
  }
  return message;
}

function joinGraphqlErrors(errors: { message?: string }[] | null | undefined): string {
  return errors?.map((e) => e.message).filter(Boolean).join('; ') ?? '';
}

/** Drop fields the deployed API schema does not know yet so listings can still save. */
async function retryProductMutationWithoutUnknownFields<T extends { errors?: { message?: string }[] | null; data: unknown }>(
  initial: T,
  payload: Record<string, unknown>,
  mutate: (next: Record<string, unknown>) => Promise<T>,
): Promise<T> {
  let res = initial;
  let next = { ...payload };
  let raw = joinGraphqlErrors(res.errors);

  for (let attempt = 0; attempt < 6 && res.errors?.length && isProductInputSchemaError(raw); attempt += 1) {
    const field = unknownProductInputField(raw);
    if (field && field in next) {
      const { [field]: _removed, ...rest } = next;
      next = rest;
    } else {
      next = stripProductExtensionFields(next);
    }
    res = await mutate(next);
    raw = joinGraphqlErrors(res.errors);
    if (!res.errors?.length) return res;
  }

  return res;
}

function mapProduct(row: Record<string, unknown>): Product {
  return {
    id: String(row.id),
    vendorId: String(row.vendorId),
    title: String(row.title),
    slug: row.slug ? String(row.slug) : undefined,
    description: row.description ? String(row.description) : undefined,
    priceCents: Number(row.priceCents),
    currency: String(row.currency ?? 'XCD'),
    ...(() => {
      const ids = normalizeProductCategoryIds([
        ...(Array.isArray(row.categoryIds) ? (row.categoryIds as string[]) : []),
        String(row.categoryId),
      ]);
      return {
        categoryId: ids[0] ?? String(row.categoryId),
        categoryIds: ids.length > 0 ? ids : undefined,
      };
    })(),
    images: normalizeImageList((row.images as string[] | undefined) ?? []),
    stock: Number(row.stock),
    sponsoredRank: row.sponsoredRank != null ? Number(row.sponsoredRank) : undefined,
    parish: row.parish ? String(row.parish) : undefined,
    variants: deserializeVariants(row.variants),
    variantMultiSelect: row.variantMultiSelect === true,
    variantConfig: parseProductVariantConfig(row.variantConfig),
    pairsWith: Array.isArray(row.pairsWith) ? (row.pairsWith as string[]) : undefined,
    listingStatus: row.listingStatus
      ? (String(row.listingStatus) as Product['listingStatus'])
      : undefined,
    categoryRequestId: row.categoryRequestId ? String(row.categoryRequestId) : undefined,
    listingNote: row.listingNote ? String(row.listingNote) : undefined,
    condition: row.condition ? (String(row.condition) as Product['condition']) : undefined,
    isLocallyMade: row.isLocallyMade === true,
    seoTitle: row.seoTitle ? String(row.seoTitle) : undefined,
    seoDescription: row.seoDescription ? String(row.seoDescription) : undefined,
    searchKeywords: Array.isArray(row.searchKeywords)
      ? (row.searchKeywords as string[])
      : undefined,
    lowStockThreshold: row.lowStockThreshold != null ? Number(row.lowStockThreshold) : undefined,
    allowBackorders: row.allowBackorders === true,
    scheduledPublishAt: row.scheduledPublishAt ? String(row.scheduledPublishAt) : undefined,
    weightKg: row.weightKg != null ? Number(row.weightKg) : undefined,
    shippingClass: row.shippingClass
      ? (String(row.shippingClass) as Product['shippingClass'])
      : undefined,
    pickupAvailable: row.pickupAvailable === true,
    pickupLocation: row.pickupLocation ? String(row.pickupLocation) : undefined,
    pickupLatitude: row.pickupLatitude != null ? Number(row.pickupLatitude) : undefined,
    pickupLongitude: row.pickupLongitude != null ? Number(row.pickupLongitude) : undefined,
    localDeliveryOnly: row.localDeliveryOnly === true,
    deliveryPriceCents:
      row.deliveryPriceCents != null ? Number(row.deliveryPriceCents) : undefined,
    deliveryParishRates: parseDeliveryParishRates(row.deliveryParishRates),
    shortDescription: row.shortDescription ? String(row.shortDescription) : undefined,
    compareAtCents: row.compareAtCents != null ? Number(row.compareAtCents) : undefined,
    costCents: row.costCents != null ? Number(row.costCents) : undefined,
    sku: row.sku ? String(row.sku) : undefined,
    barcode: row.barcode ? String(row.barcode) : undefined,
    taxable: row.taxable !== false,
    lengthCm: row.lengthCm != null ? Number(row.lengthCm) : undefined,
    widthCm: row.widthCm != null ? Number(row.widthCm) : undefined,
    heightCm: row.heightCm != null ? Number(row.heightCm) : undefined,
    imageAlts: Array.isArray(row.imageAlts)
      ? (row.imageAlts as string[])
      : Array.isArray(row.altTexts)
        ? (row.altTexts as string[])
        : undefined,
    seoStatus: row.seoStatus ? (String(row.seoStatus) as Product['seoStatus']) : undefined,
    featured: row.featured === true,
    soldCount: row.soldCount != null ? Number(row.soldCount) : undefined,
    reportCount: row.reportCount != null ? Number(row.reportCount) : undefined,
    createdAt: String(row.createdAt ?? nowIso()),
    updatedAt: String(row.updatedAt ?? nowIso()),
  };
}

function mapProductQuestion(row: Record<string, unknown>): ProductQuestion {
  return {
    id: String(row.id),
    productId: String(row.productId),
    productOwnerUserId: String(row.productOwnerUserId),
    userId: String(row.userId),
    userDisplayName: String(row.userDisplayName),
    question: String(row.question),
    answer: row.answer ? String(row.answer) : undefined,
    answeredAt: row.answeredAt ? String(row.answeredAt) : undefined,
    answeredByUserId: row.answeredByUserId ? String(row.answeredByUserId) : undefined,
    createdAt: String(row.createdAt ?? nowIso()),
    updatedAt: String(row.updatedAt ?? nowIso()),
  };
}

function parseVendorDisplayFont(value: unknown): Vendor['displayFont'] | undefined {
  const s = typeof value === 'string' ? value.trim() : '';
  if (s === 'platform' || s === 'serif' || s === 'modern') return s;
  return undefined;
}

function mapVendor(row: Record<string, unknown>): Vendor {
  return {
    id: String(row.id),
    ownerUserId: String(row.ownerUserId),
    businessName: String(row.businessName),
    businessOwnerName: row.businessOwnerName ? String(row.businessOwnerName) : undefined,
    slug: String(row.slug),
    description: row.description ? String(row.description) : undefined,
    logoUrl: row.logoUrl ? String(row.logoUrl) : undefined,
    brandColor: row.brandColor ? String(row.brandColor) : undefined,
    mission: row.mission ? String(row.mission) : undefined,
    vision: row.vision ? String(row.vision) : undefined,
    valuesText: row.valuesText ? String(row.valuesText) : undefined,
    displayFont: parseVendorDisplayFont(row.displayFont),
    phone: row.phone ? String(row.phone) : undefined,
    website: row.website ? String(row.website) : undefined,
    instagramUrl: row.instagramUrl ? String(row.instagramUrl) : undefined,
    facebookUrl: row.facebookUrl ? String(row.facebookUrl) : undefined,
    tiktokUrl: row.tiktokUrl ? String(row.tiktokUrl) : undefined,
    linkedinUrl: row.linkedinUrl ? String(row.linkedinUrl) : undefined,
    youtubeUrl: row.youtubeUrl ? String(row.youtubeUrl) : undefined,
    xUrl: row.xUrl ? String(row.xUrl) : undefined,
    ratingAvg: Number(row.ratingAvg ?? 0),
    ratingCount: Number(row.ratingCount ?? 0),
    payoutsEnabled: false,
    acceptsCardPayments: row.acceptsCardPayments === true,
    offersCod: row.offersCod === false ? false : true,
    onlineOnly: row.onlineOnly === true,
    offersPickup:
      row.onlineOnly === true ? false : row.offersPickup === false ? false : true,
    offersDelivery: row.offersDelivery === false ? false : true,
    pickupAddress: row.pickupAddress ? String(row.pickupAddress) : undefined,
    pickupLatitude: row.pickupLatitude != null ? Number(row.pickupLatitude) : undefined,
    pickupLongitude: row.pickupLongitude != null ? Number(row.pickupLongitude) : undefined,
    defaultProductPickupAvailable:
      row.defaultProductPickupAvailable === true
        ? true
        : row.defaultProductPickupAvailable === false
          ? false
          : undefined,
    defaultProductLocalDeliveryOnly:
      row.defaultProductLocalDeliveryOnly === true
        ? true
        : row.defaultProductLocalDeliveryOnly === false
          ? false
          : undefined,
    defaultProductDeliveryPriceCents:
      row.defaultProductDeliveryPriceCents != null
        ? Number(row.defaultProductDeliveryPriceCents)
        : undefined,
    defaultProductDeliveryParishRates: parseDeliveryParishRates(
      row.defaultProductDeliveryParishRates,
    ),
    pickupParish: row.pickupParish ? String(row.pickupParish) : undefined,
    pickupParishes: Array.isArray(row.pickupParishes)
      ? (row.pickupParishes as unknown[]).map((p) => String(p)).filter(Boolean)
      : undefined,
    verified: row.verified === true,
    verifiedAt: row.verifiedAt ? String(row.verifiedAt) : undefined,
    giftCertsEnabled: row.giftCertsEnabled === true,
    giftCertDenominationsCents: Array.isArray(row.giftCertDenominationsCents)
      ? (row.giftCertDenominationsCents as number[])
      : undefined,
    giftCertCustomMessage: row.giftCertCustomMessage ? String(row.giftCertCustomMessage) : undefined,
    approvalStatus: (row.approvalStatus as Vendor['approvalStatus']) ?? 'none',
    giftCertApprovalStatus: (row.giftCertApprovalStatus as Vendor['giftCertApprovalStatus']) ?? 'none',
    giftCertApprovalNote: row.giftCertApprovalNote ? String(row.giftCertApprovalNote) : undefined,
    cardPaymentApprovalStatus:
      (row.cardPaymentApprovalStatus as Vendor['cardPaymentApprovalStatus']) ?? 'none',
    cardPaymentApprovalNote: row.cardPaymentApprovalNote
      ? String(row.cardPaymentApprovalNote)
      : undefined,
    propertyListingApprovalStatus:
      (row.propertyListingApprovalStatus as Vendor['propertyListingApprovalStatus']) ?? 'none',
    propertyListingApprovalNote: row.propertyListingApprovalNote
      ? String(row.propertyListingApprovalNote)
      : undefined,
    subscriptionPlanId: row.subscriptionPlanId ? String(row.subscriptionPlanId) : undefined,
    subscriptionTrialEndsAt: row.subscriptionTrialEndsAt ? String(row.subscriptionTrialEndsAt) : undefined,
    stripeCustomerId: row.stripeCustomerId ? String(row.stripeCustomerId) : undefined,
    stripeSubscriptionId: row.stripeSubscriptionId ? String(row.stripeSubscriptionId) : undefined,
    subscriptionStatus: row.subscriptionStatus ? String(row.subscriptionStatus) : undefined,
    subscriptionCurrentPeriodEnd: row.subscriptionCurrentPeriodEnd
      ? String(row.subscriptionCurrentPeriodEnd)
      : undefined,
    subscriptionWaived: row.subscriptionWaived === true,
    subscriptionWaivedUntil: row.subscriptionWaivedUntil ? String(row.subscriptionWaivedUntil) : undefined,
    subscriptionWaivedNote: row.subscriptionWaivedNote ? String(row.subscriptionWaivedNote) : undefined,
    commissionBps: row.commissionBps != null ? Number(row.commissionBps) : undefined,
    businessType: row.businessType ? String(row.businessType) : undefined,
    directorySector: row.directorySector
      ? (String(row.directorySector) as Vendor['directorySector'])
      : undefined,
    directoryCategoryId: row.directoryCategoryId ? String(row.directoryCategoryId) : undefined,
    directoryCategoryCustomLabel: row.directoryCategoryCustomLabel
      ? String(row.directoryCategoryCustomLabel)
      : undefined,
    businessModes: Array.isArray(row.businessModes)
      ? (row.businessModes as Vendor['businessModes'])
      : undefined,
    primaryMode: row.primaryMode ? (String(row.primaryMode) as Vendor['primaryMode']) : undefined,
    linkedProId: row.linkedProId ? String(row.linkedProId) : undefined,
    isRegisteredBusiness: row.isRegisteredBusiness === true,
    businessRegistrationNumber: row.businessRegistrationNumber
      ? String(row.businessRegistrationNumber)
      : undefined,
    taxId: row.taxId ? String(row.taxId) : undefined,
    registrationDocumentKey: row.registrationDocumentKey
      ? String(row.registrationDocumentKey)
      : undefined,
    realEstateLicenseNumber: row.realEstateLicenseNumber
      ? String(row.realEstateLicenseNumber)
      : undefined,
    primaryColor: row.primaryColor ? String(row.primaryColor) : undefined,
    accentColor: row.accentColor ? String(row.accentColor) : undefined,
    heroTagline: row.heroTagline ? String(row.heroTagline) : undefined,
    serviceAreas: Array.isArray(row.serviceAreas)
      ? (row.serviceAreas as unknown[]).map((p) => String(p)).filter(Boolean)
      : undefined,
    bioCopy: row.bioCopy ? String(row.bioCopy) : undefined,
    priceNote: row.priceNote ? String(row.priceNote) : undefined,
    podEnabled: row.podEnabled === true,
    procurementEnabled: row.procurementEnabled === true,
    bookingNotifyChannels: parseBookingNotifyChannels(row.bookingNotifyChannels),
    seoScore: row.seoScore != null ? Number(row.seoScore) : undefined,
    trustedPartner: row.trustedPartner === true,
    partnerBusinessIds: Array.isArray(row.partnerBusinessIds)
      ? (row.partnerBusinessIds as unknown[]).map((id) => String(id)).filter(Boolean)
      : undefined,
    seoNudgeSentAt: row.seoNudgeSentAt ? String(row.seoNudgeSentAt) : undefined,
    createdAt: String(row.createdAt ?? nowIso()),
  };
}

function mapClassified(row: Record<string, unknown>): Classified {
  const category = (row.category as Classified['category'] | undefined) ?? 'general';
  const sellerType = (row.sellerType as Classified['sellerType'] | undefined) ?? 'individual';
  return {
    id: String(row.id),
    sellerUserId: String(row.sellerUserId),
    title: String(row.title),
    description: row.description ? String(row.description) : undefined,
    priceCents: Number(row.priceCents),
    currency: String(row.currency ?? 'XCD'),
    images: asStringArray(row.images),
    parish: String(row.parish),
    status: row.status as ClassifiedStatus,
    reportCount: Number(row.reportCount ?? 0),
    category,
    listingIntent: row.listingIntent as Classified['listingIntent'] | undefined,
    propertyType: row.propertyType as Classified['propertyType'] | undefined,
    bedrooms: row.bedrooms != null ? Number(row.bedrooms) : undefined,
    bathrooms: row.bathrooms != null ? Number(row.bathrooms) : undefined,
    sellerType,
    vendorId: row.vendorId ? String(row.vendorId) : undefined,
    slug: row.slug ? String(row.slug) : undefined,
    areaLabel: row.areaLabel ? String(row.areaLabel) : undefined,
    latitude: row.latitude != null ? Number(row.latitude) : undefined,
    longitude: row.longitude != null ? Number(row.longitude) : undefined,
    sqftInterior: row.sqftInterior != null ? Number(row.sqftInterior) : undefined,
    sqftLot: row.sqftLot != null ? Number(row.sqftLot) : undefined,
    furnished: row.furnished as Classified['furnished'] | undefined,
    amenities: (() => {
      const list = asStringArray(row.amenities);
      return list.length ? list : undefined;
    })(),
    yearBuilt: row.yearBuilt != null ? Number(row.yearBuilt) : undefined,
    parkingSpaces: row.parkingSpaces != null ? Number(row.parkingSpaces) : undefined,
    virtualTourUrl: row.virtualTourUrl ? String(row.virtualTourUrl) : undefined,
    contactPolicy: row.contactPolicy as Classified['contactPolicy'] | undefined,
    contactPhone: row.contactPhone ? String(row.contactPhone) : undefined,
    contactEmail: row.contactEmail ? String(row.contactEmail) : undefined,
    listingAgentName: row.listingAgentName ? String(row.listingAgentName) : undefined,
    imageAlts: (() => {
      const list = asStringArray(row.imageAlts);
      return list.length ? list : undefined;
    })(),
    seoTitle: row.seoTitle ? String(row.seoTitle) : undefined,
    seoDescription: row.seoDescription ? String(row.seoDescription) : undefined,
    seoStatus: row.seoStatus
      ? (String(row.seoStatus) as Classified['seoStatus'])
      : undefined,
    createdAt: String(row.createdAt ?? nowIso()),
    updatedAt: String(row.updatedAt ?? nowIso()),
  };
}

function matchesClassifiedFilters(c: Classified, filters?: ListClassifiedsFilters): boolean {
  if (!filters) return true;
  if (filters.excludeCategory && c.category === filters.excludeCategory) return false;
  if (filters.category && c.category !== filters.category) return false;
  if (filters.freeOnly && c.priceCents !== 0) return false;
  if (filters.listingIntent && c.listingIntent !== filters.listingIntent) return false;
  if (filters.propertyType && c.propertyType !== filters.propertyType) return false;
  if (filters.parish && c.parish !== filters.parish) return false;
  if (filters.minBedrooms != null && (c.bedrooms ?? 0) < filters.minBedrooms) return false;
  if (filters.query) {
    const q = filters.query.toLowerCase();
    const hay = `${c.title} ${c.description ?? ''}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  return true;
}

/** Options for marketplace / shop catalog queries (AppSync filter + client post-filter). */
export type ProductListOptions = {
  query?: string;
  parish?: string;
  categoryId?: string;
  /** When set, matches any of these category ids (parent + descendants). */
  categoryIds?: string[];
  minPriceCents?: number;
  maxPriceCents?: number;
  inStockOnly?: boolean;
  condition?: Product['condition'];
  isLocallyMade?: boolean;
  shippingClass?: Product['shippingClass'];
  stockStatus?: StockStatusFilter;
  /** Default true for public shop — only `published` rows (legacy unset rows included client-side). */
  publishedOnly?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildProductListFilter(opts?: ProductListOptions): any {
  const parts: Record<string, unknown>[] = [];
  if (opts?.categoryId) {
    parts.push({
      or: [
        { categoryId: { eq: opts.categoryId } },
        { categoryIds: { contains: opts.categoryId } },
      ],
    });
  } else if (opts?.categoryIds?.length) {
    const ids = opts.categoryIds;
    parts.push({
      or: ids.flatMap((id) => [
        { categoryId: { eq: id } },
        { categoryIds: { contains: id } },
      ]),
    });
  }
  if (opts?.parish) parts.push({ parish: { eq: opts.parish } });
  if (opts?.minPriceCents != null) parts.push({ priceCents: { ge: opts.minPriceCents } });
  if (opts?.maxPriceCents != null) parts.push({ priceCents: { le: opts.maxPriceCents } });
  if (opts?.inStockOnly) parts.push({ stock: { gt: 0 } });
  if (opts?.condition) parts.push({ condition: { eq: opts.condition } });
  if (opts?.isLocallyMade === true) parts.push({ isLocallyMade: { eq: true } });
  if (opts?.shippingClass) parts.push({ shippingClass: { eq: opts.shippingClass } });
  if (parts.length === 0) return undefined;
  if (parts.length === 1) return parts[0];
  return { and: parts };
}

async function listProductRecords(
  client: AnyDataClient,
  filter?: ReturnType<typeof buildProductListFilter>,
): Promise<Record<string, unknown>[]> {
  const items: Record<string, unknown>[] = [];
  let nextToken: string | undefined;
  do {
    const res = await client.models.Product.list({
      filter,
      nextToken,
      limit: 100,
    });
    items.push(...((res.data ?? []) as Record<string, unknown>[]));
    nextToken = res.nextToken ?? undefined;
  } while (nextToken);
  return items;
}

function applyProductClientFilters(products: Product[], opts?: ProductListOptions): Product[] {
  let list = [...products];
  const publishedOnly = opts?.publishedOnly !== false;
  if (publishedOnly) {
    list = list.filter(isPublishedProduct);
  }
  if (opts?.stockStatus) {
    list = list.filter((p) => productStockStatus(p) === opts.stockStatus);
  }
  if (opts?.categoryIds?.length) {
    list = list.filter((p) => productMatchesCategoryFilter(p, opts.categoryIds!));
  } else if (opts?.categoryId) {
    list = list.filter((p) => productMatchesCategoryFilter(p, [opts.categoryId!]));
  }
  if (opts?.query) {
    const q = opts.query.toLowerCase();
    list = list.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        (p.description ?? '').toLowerCase().includes(q) ||
        (p.slug ?? '').toLowerCase().includes(q) ||
        (p.searchKeywords ?? []).some((kw) => kw.toLowerCase().includes(q)),
    );
  }
  list.sort((a, b) => (b.sponsoredRank ?? 0) - (a.sponsoredRank ?? 0));
  return list;
}

function sortProductsForShelf(products: Product[], query?: string) {
  return applyProductClientFilters(products, { query, publishedOnly: true });
}

/** Propagates API errors — use from shop and other surfaces that must distinguish empty vs failed. */
export async function listProductsStrict(opts?: ProductListOptions): Promise<Product[]> {
  const client = await getCatalogClient();
  const filter = buildProductListFilter(opts);
  const items = await listProductRecords(client, filter);
  return applyProductClientFilters(items.map(mapProduct), opts);
}

export async function listProducts(opts?: ProductListOptions): Promise<Product[]> {
  try {
    return await listProductsStrict(opts);
  } catch (e) {
    console.error('[ShopGND] listProducts failed', e);
    return [];
  }
}

export async function searchProducts(
  query: string,
  filters?: Omit<ProductListOptions, 'query'>,
): Promise<Product[]> {
  return listProducts({ ...filters, query });
}

export async function listProductsBySlug(slug: string): Promise<Product | undefined> {
  const normalized = slug.trim().replace(/\/+$/, '').toLowerCase();
  if (!normalized) return undefined;
  try {
    const client = await getCatalogClient();
    const productBySlug = client.models.Product?.productBySlug;
    if (typeof productBySlug === 'function') {
      const res = await productBySlug({ slug: normalized });
      const row = (res.data ?? [])[0] as Record<string, unknown> | undefined;
      if (row) return mapProduct(row);
    }
    const res = await client.models.Product.list({
      filter: { slug: { eq: normalized } },
      limit: 1,
    });
    const row = (res.data ?? [])[0] as Record<string, unknown> | undefined;
    if (row) return mapProduct(row);
    const items = await listProductRecords(client, undefined);
    return items
      .map(mapProduct)
      .find((p) => (p.slug ?? '').toLowerCase() === normalized);
  } catch (e) {
    console.error('[ShopGND] listProductsBySlug failed', e);
    return undefined;
  }
}

export async function allocateProductSlug(
  title: string,
  parish?: string,
  excludeId?: string,
): Promise<string> {
  for (let attempt = 0; attempt < 12; attempt++) {
    const candidate = buildListingSlug(title, parish ?? 'grenada', 'grenada');
    const existing = await listProductsBySlug(candidate);
    if (!existing || (excludeId && existing.id === excludeId)) return candidate;
  }
  return buildListingSlug(title, parish ?? 'grenada', 'grenada', String(Date.now()));
}

/** Resolve storefront product by SEO slug or product id (legacy /shop links). */
export async function getPublicProductBySlug(
  slugOrId: string,
  viewerUserId?: string | null,
): Promise<Product | undefined> {
  const trimmed = slugOrId.trim().replace(/\/+$/, '');
  if (!trimmed) return undefined;
  if (isVendorUuid(trimmed)) {
    return getPublicProduct(trimmed, viewerUserId);
  }
  const bySlug = await listProductsBySlug(trimmed);
  if (bySlug) return getPublicProduct(bySlug.id, viewerUserId);
  return undefined;
}

export async function listSimilarProducts(
  product: Product,
  limit = 4,
): Promise<Product[]> {
  const items = await listProducts({
    parish: product.parish,
    publishedOnly: true,
  });
  return items
    .filter(
      (p) =>
        p.id !== product.id &&
        p.categoryId === product.categoryId &&
        (p.seoStatus == null || p.seoStatus === 'active'),
    )
    .slice(0, limit);
}

export async function listFeaturedProducts(limit = 6): Promise<Product[]> {
  const items = await listProducts({ publishedOnly: true });
  const active = items.filter((p) => !p.seoStatus || p.seoStatus === 'active');
  const featured = active.filter((p) => p.featured);
  if (featured.length >= limit) return featured.slice(0, limit);
  return active.slice(0, limit);
}

export async function getProduct(id: string): Promise<Product | undefined> {
  try {
    const client = await getCatalogClient();
    const res = await client.models.Product.get({ id });
    return res.data ? mapProduct(res.data as Record<string, unknown>) : undefined;
  } catch (e) {
    console.error('[ShopGND] getProduct failed', e);
    return undefined;
  }
}

/** Storefront-safe read — unpublished listings only visible to the owning vendor. */
export async function getPublicProduct(
  id: string,
  viewerUserId?: string | null,
): Promise<Product | undefined> {
  const product = await getProduct(id);
  if (!product) return undefined;
  if (isPublishedProduct(product)) return product;
  if (!viewerUserId) return undefined;
  const vendor = await getVendor(product.vendorId);
  if (vendor?.ownerUserId === viewerUserId) return product;
  return undefined;
}

export async function updateProductListingAdmin(
  productId: string,
  patch: Partial<ProductDraft>,
): Promise<Product | null> {
  const client = await getAuthedDataClient();
  const payload: Record<string, unknown> = { id: productId };
  const fields: (keyof ProductDraft)[] = [
    'categoryId',
    'categoryIds',
    'listingStatus',
    'categoryRequestId',
    'listingNote',
    'title',
    'slug',
    'description',
    'priceCents',
    'currency',
    'images',
    'stock',
    'parish',
    'sponsoredRank',
    'variants',
    'variantMultiSelect',
    'variantConfig',
    'pairsWith',
    'condition',
    'isLocallyMade',
    'seoTitle',
    'seoDescription',
    'searchKeywords',
    'lowStockThreshold',
    'allowBackorders',
    'scheduledPublishAt',
    'weightKg',
    'shippingClass',
    'pickupAvailable',
    'pickupLocation',
    'pickupLatitude',
    'pickupLongitude',
    'localDeliveryOnly',
    'deliveryPriceCents',
    'deliveryParishRates',
    'shortDescription',
    'compareAtCents',
    'costCents',
    'sku',
    'barcode',
    'taxable',
    'lengthCm',
    'widthCm',
    'heightCm',
    'imageAlts',
    'seoStatus',
    'soldCount',
  ];
  for (const key of fields) {
    if (patch[key] === undefined) continue;
    const value = patch[key];
    if (key === 'categoryRequestId' && !value) {
      payload.categoryRequestId = null;
    } else if (key === 'pairsWith' && Array.isArray(value)) {
      payload.pairsWith = value.length ? value : null;
    } else if (key === 'variants') {
      payload.variants = serializeVariants(value as ProductVariant[] | undefined);
    } else if (key === 'images') {
      payload.images = normalizeImageList(value as string[]);
    } else {
      payload[key] = value;
    }
  }
  const res = await client.models.Product.update(payload);
  if (!res.data) return null;
  return mapProduct(res.data as Record<string, unknown>);
}

export async function listProductsByIds(ids: string[]): Promise<Product[]> {
  if (ids.length === 0) return [];
  const results = await Promise.all(ids.map((id) => getProduct(id)));
  return results.filter((p): p is Product => p != null);
}

export async function listAllProducts(): Promise<Product[]> {
  try {
    const client = await getCatalogClient();
    const res = await client.models.Product.list();
    return ((res.data ?? []) as Record<string, unknown>[]).map(mapProduct);
  } catch (e) {
    console.error('[ShopGND] listAllProducts failed', e);
    return [];
  }
}

export async function deleteProduct(productId: string): Promise<void> {
  const client = await getAuthedDataClient();
  const res = await client.models.Product.delete({ id: productId });
  if (res.errors?.length) {
    throw new Error(
      res.errors.map((e: { message?: string }) => e.message).filter(Boolean).join('; '),
    );
  }
}

async function listAllVendorsFromClient(client: AnyDataClient): Promise<Vendor[]> {
  const items: Vendor[] = [];
  let nextToken: string | undefined;
  do {
    const res = await client.models.Vendor.list({
      limit: 200,
      ...(nextToken ? { nextToken } : {}),
    });
    items.push(
      ...((res.data ?? []) as Record<string, unknown>[])
        .filter((row) => row?.id != null && row.businessName != null)
        .map(mapVendor),
    );
    nextToken = res.nextToken ?? undefined;
  } while (nextToken);
  return items;
}

export async function listVendorsStrict(): Promise<Vendor[]> {
  const client = await getCatalogClient();
  return listAllVendorsFromClient(client);
}

export async function listVendors(): Promise<Vendor[]> {
  try {
    return await listVendorsStrict();
  } catch (e) {
    console.error('[ShopGND] listVendors failed', e);
    return [];
  }
}

export async function listProductsByCategoryRequestId(categoryRequestId: string): Promise<Product[]> {
  try {
    const client = await getCatalogClient();
    const res = await client.models.Product.list({
      filter: { categoryRequestId: { eq: categoryRequestId } },
    });
    return ((res.data ?? []) as Record<string, unknown>[]).map(mapProduct);
  } catch {
    return [];
  }
}

export async function listProductsByVendor(
  vendorId: string,
  opts?: { publishedOnly?: boolean },
): Promise<Product[]> {
  try {
    const client = await getCatalogClient();
    const items = await listProductRecords(client, { vendorId: { eq: vendorId } });
    const mapped = items.map(mapProduct);
    return opts?.publishedOnly ? mapped.filter(isPublishedProduct) : mapped;
  } catch (e) {
    console.error('[ShopGND] listProductsByVendor failed', e);
    return [];
  }
}

/** Other listings from the same vendor, excluding the product being viewed. */
export async function listOtherProductsByVendor(
  vendorId: string,
  excludeProductId: string,
  limit = 4,
): Promise<Product[]> {
  const items = await listProductsByVendor(vendorId, { publishedOnly: true });
  return items.filter((p) => p.id !== excludeProductId).slice(0, limit);
}

export async function getVendor(id: string): Promise<Vendor | undefined> {
  try {
    const client = await getCatalogClient();
    const res = await client.models.Vendor.get({ id });
    return res.data ? mapVendor(res.data as Record<string, unknown>) : undefined;
  } catch (e) {
    console.error('[ShopGND] getVendor failed', e);
    return undefined;
  }
}

export async function getVendorBySlug(slug: string): Promise<Vendor | undefined> {
  const normalized = slug.trim().toLowerCase();
  if (!normalized) return undefined;

  try {
    const client = await getCatalogClient();
    const res = await client.models.Vendor.list({
      filter: { slug: { eq: normalized } },
    });
    const row = (res.data ?? [])[0] as Record<string, unknown> | undefined;
    if (row) return mapVendor(row);
  } catch (e) {
    console.warn('[ShopGND] getVendorBySlug filter lookup failed', e);
  }

  try {
    const client = await getCatalogClient();
    const all = await listAllVendorsFromClient(client);
    return all.find((v) => v.slug.toLowerCase() === normalized);
  } catch (e) {
    console.error('[ShopGND] getVendorBySlug failed', e);
    return undefined;
  }
}

export async function getVendorForOwnerUser(userId: string): Promise<Vendor | undefined> {
  try {
    const client = await getCatalogClient();
    const res = await client.models.Vendor.list({
      filter: { ownerUserId: { eq: userId } },
    });
    const row = (res.data ?? [])[0] as Record<string, unknown> | undefined;
    if (row) return mapVendor(row);
    const all = await listAllVendorsFromClient(client);
    return all.find((v) => v.ownerUserId === userId);
  } catch (e) {
    console.error('[ShopGND] getVendorForOwnerUser failed', e);
    return undefined;
  }
}

export async function getVendorByLinkedProId(proId: string): Promise<Vendor | undefined> {
  try {
    const client = await getCatalogClient();
    const res = await client.models.Vendor.list({
      filter: { linkedProId: { eq: proId } },
    });
    const row = (res.data ?? [])[0] as Record<string, unknown> | undefined;
    return row ? mapVendor(row) : undefined;
  } catch (e) {
    console.error('[ShopGND] getVendorByLinkedProId failed', e);
    return undefined;
  }
}

/** Resolve /store/:param from slug or legacy vendor UUID. */
export async function resolveVendorFromStoreParam(param: string): Promise<Vendor | undefined> {
  const trimmed = param.trim();
  if (!trimmed) return undefined;
  if (isVendorUuid(trimmed)) {
    const byId = await getVendor(trimmed);
    if (byId) return byId;
  } else {
    const bySlug = await getVendorBySlug(trimmed);
    if (bySlug) return bySlug;
    const byId = await getVendor(trimmed);
    if (byId) return byId;
  }
  return undefined;
}

export type ResolvedBusiness = {
  vendor: Vendor;
  linkedPro?: ServiceProvider;
};

export async function resolveBusinessFromParam(param: string): Promise<ResolvedBusiness | undefined> {
  const trimmed = param.trim();
  if (!trimmed) return undefined;

  // Hire-only pros use synthetic slugs `pro-{idPrefix}` — resolve before vendor slug lookup.
  if (parseProBusinessSlug(trimmed)) {
    const proBusiness = await resolveStandaloneProBusiness(trimmed);
    if (proBusiness) return proBusiness;
  }

  const vendor = await resolveVendorFromStoreParam(trimmed);
  if (vendor) {
    const linkedPro = vendor.linkedProId
      ? await getServiceProvider(vendor.linkedProId)
      : undefined;
    return { vendor, linkedPro };
  }

  return resolveStandaloneProBusiness(trimmed);
}

async function resolveStandaloneProBusiness(param: string): Promise<ResolvedBusiness | undefined> {
  let pro = await getServiceProvider(param);
  if (!pro) {
    const prefix = parseProBusinessSlug(param);
    if (prefix) pro = await getServiceProviderByIdPrefix(prefix);
  }
  if (!pro) return undefined;

  const linkedVendor = await getVendorByLinkedProId(pro.id);
  if (linkedVendor) {
    return { vendor: linkedVendor, linkedPro: pro };
  }

  try {
    const vendor = await ensureVendorLinkedToPro(pro);
    return { vendor, linkedPro: pro };
  } catch (e) {
    console.warn('[ShopGND] ensureVendorLinkedToPro failed, using shell', e);
    return { vendor: buildProVendorShell(pro), linkedPro: pro };
  }
}

async function getServiceProviderByIdPrefix(prefix: string): Promise<ServiceProvider | undefined> {
  const normalized = prefix.trim().toLowerCase();
  if (!normalized) return undefined;
  try {
    const client = await getCatalogClient();
    const list = await listAllServiceProvidersFromClient(client);
    const matches = list.filter((p) => p.id.toLowerCase().startsWith(normalized));
    if (matches.length === 1) return matches[0];
    if (matches.length > 1) {
      return matches.sort((a, b) => a.id.length - b.id.length)[0];
    }
  } catch (e) {
    console.error('[ShopGND] getServiceProviderByIdPrefix failed', e);
  }
  return undefined;
}

export async function getServiceProviderForOwnerUser(
  userId: string,
): Promise<ServiceProvider | undefined> {
  try {
    const client = await getCatalogClient();
    const res = await client.models.ServiceProvider.list({
      filter: { ownerUserId: { eq: userId } },
    });
    const row = (res.data ?? [])[0] as Record<string, unknown> | undefined;
    return row ? mapServiceProvider(row) : undefined;
  } catch (e) {
    console.error('[ShopGND] getServiceProviderForOwnerUser failed', e);
    return undefined;
  }
}

async function listAllServiceProvidersFromClient(client: AnyDataClient): Promise<ServiceProvider[]> {
  const items: ServiceProvider[] = [];
  let nextToken: string | undefined;
  do {
    const res = await client.models.ServiceProvider.list({
      limit: 200,
      ...(nextToken ? { nextToken } : {}),
    });
    items.push(...((res.data ?? []) as Record<string, unknown>[]).map(mapServiceProvider));
    nextToken = res.nextToken ?? undefined;
  } while (nextToken);
  return items;
}

export async function listServiceProviders(filters?: {
  parish?: string;
  categoryId?: string;
}): Promise<ServiceProvider[]> {
  try {
    const client = await getCatalogClient();
    let list = await listAllServiceProvidersFromClient(client);
    if (filters?.parish) list = list.filter((s) => s.parish === filters.parish);
    if (filters?.categoryId) {
      list = list.filter((s) => s.categories.includes(filters.categoryId!));
    }
    return list;
  } catch (e) {
    console.error('[ShopGND] listServiceProviders failed', e);
    return [];
  }
}

export async function getServiceProvider(id: string): Promise<ServiceProvider | undefined> {
  try {
    const client = await getCatalogClient();
    const res = await client.models.ServiceProvider.get({ id });
    return res.data ? mapServiceProvider(res.data as Record<string, unknown>) : undefined;
  } catch (e) {
    console.error('[ShopGND] getServiceProvider failed', e);
    return undefined;
  }
}

function mapServiceProvider(row: Record<string, unknown>): ServiceProvider {
  return {
    id: String(row.id),
    ownerUserId: String(row.ownerUserId),
    providerType: (row.providerType as ServiceProvider['providerType']) ?? 'individual',
    headline: String(row.headline),
    bio: row.bio ? String(row.bio) : undefined,
    categories: (row.categories as string[] | undefined) ?? [],
    parish: String(row.parish),
    hourlyRateCents: row.hourlyRateCents != null ? Number(row.hourlyRateCents) : undefined,
    ratingAvg: Number(row.ratingAvg ?? 0),
    ratingCount: Number(row.ratingCount ?? 0),
    avatarUrl: row.avatarUrl ? String(row.avatarUrl) : undefined,
    phone: row.phone ? String(row.phone) : undefined,
    companyName: row.companyName ? String(row.companyName) : undefined,
    logoUrl: row.logoUrl ? String(row.logoUrl) : undefined,
    website: row.website ? String(row.website) : undefined,
    instagramUrl: row.instagramUrl ? String(row.instagramUrl) : undefined,
    facebookUrl: row.facebookUrl ? String(row.facebookUrl) : undefined,
    tiktokUrl: row.tiktokUrl ? String(row.tiktokUrl) : undefined,
    linkedinUrl: row.linkedinUrl ? String(row.linkedinUrl) : undefined,
    youtubeUrl: row.youtubeUrl ? String(row.youtubeUrl) : undefined,
    xUrl: row.xUrl ? String(row.xUrl) : undefined,
    teamSize: row.teamSize != null ? Number(row.teamSize) : undefined,
    licenseNumber: row.licenseNumber ? String(row.licenseNumber) : undefined,
    createdAt: String(row.createdAt ?? nowIso()),
  };
}

export type ServiceProDraft = {
  ownerUserId: string;
  providerType?: ServiceProvider['providerType'];
  headline: string;
  bio?: string;
  categories: string[];
  parish: string;
  hourlyRateCents?: number;
  avatarUrl?: string;
  companyName?: string;
  logoUrl?: string;
  website?: string;
  instagramUrl?: string;
  facebookUrl?: string;
  tiktokUrl?: string;
  linkedinUrl?: string;
  youtubeUrl?: string;
  xUrl?: string;
  teamSize?: number;
  licenseNumber?: string;
};

export async function createServiceProRecord(draft: ServiceProDraft): Promise<ServiceProvider> {
  const client = await getAuthedDataClient();
  const res = await client.models.ServiceProvider.create({
    ...draft,
    ratingAvg: 0,
    ratingCount: 0,
  });
  if (!res.data) throw new Error('Could not create service provider');
  const pro = mapServiceProvider(res.data as Record<string, unknown>);
  try {
    const { linkProToOwnerOrg, invalidateWorkspaceQuery } = await import('@/api/orgs');
    await linkProToOwnerOrg(pro);
    invalidateWorkspaceQuery(pro.ownerUserId);
  } catch (e) {
    console.warn('[ShopGND] linkProToOwnerOrg failed', e);
  }
  try {
    await assignHubRole('service_provider');
    await refreshAuthUser({ forceRefresh: true });
  } catch (e) {
    console.warn('[ShopGND] assignHubRole(service_provider) failed', e);
  }
  try {
    await ensureVendorLinkedToPro(pro);
  } catch (e) {
    console.warn('[ShopGND] ensureVendorLinkedToPro failed', e);
  }
  return pro;
}

export async function updateServiceProRecord(
  proId: string,
  patch: Partial<ServiceProDraft>,
): Promise<ServiceProvider | null> {
  const client = await getAuthedDataClient();
  const res = await client.models.ServiceProvider.update({ id: proId, ...patch });
  return res.data ? mapServiceProvider(res.data as Record<string, unknown>) : null;
}

export async function deleteServiceProRecord(proId: string): Promise<boolean> {
  try {
    const client = await getAuthedDataClient();
    await client.models.ServiceProvider.delete({ id: proId });
    return true;
  } catch {
    return false;
  }
}

export type ListClassifiedsFilters = {
  parish?: string;
  query?: string;
  category?: Classified['category'];
  excludeCategory?: Classified['category'];
  /** When true, only listings with priceCents === 0. */
  freeOnly?: boolean;
  listingIntent?: Classified['listingIntent'];
  propertyType?: Classified['propertyType'];
  minBedrooms?: number;
  minPriceCents?: number;
  maxPriceCents?: number;
  furnished?: Classified['furnished'];
  amenities?: string[];
  hasMapPin?: boolean;
  vendorId?: string;
};

export async function listClassifieds(filters?: ListClassifiedsFilters): Promise<Classified[]> {
  try {
    const client = await getCatalogClient();
    const res = await client.models.Classified.list({
      filter: {
        status: { eq: 'active' },
        ...(filters?.category
          ? { category: { eq: filters.category } }
          : filters?.excludeCategory
            ? { category: { ne: filters.excludeCategory } }
            : {}),
      },
    });
    let list = ((res.data ?? []) as Record<string, unknown>[]).map(mapClassified);
    list = list.filter((c) => matchesClassifiedFilters(c, filters));
    return list;
  } catch (e) {
    console.error('[ShopGND] listClassifieds failed', e);
    return [];
  }
}

export async function updateProductStock(
  productId: string,
  stock: number,
  _reason: 'restock' | 'adjustment' = 'restock',
  _note?: string,
): Promise<Product | null> {
  try {
    const existing = await getProduct(productId);
    if (!existing) return null;
    const { hubUpdateProductStock, isHubApiConfigured } = await import('@/api/hub');
    if (await isHubApiConfigured()) {
      await hubUpdateProductStock(productId, existing.vendorId, Math.max(0, stock));
      return (await getProduct(productId)) ?? { ...existing, stock: Math.max(0, stock) };
    }
    const { updateProductViaLambda } = await import('@/api/productListing');
    await updateProductViaLambda(productId, existing.vendorId, { stock: Math.max(0, stock) });
    return (await getProduct(productId)) ?? { ...existing, stock: Math.max(0, stock) };
  } catch (e) {
    console.error('[ShopGND] updateProductStock failed', e);
    return null;
  }
}

function omitUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined),
  ) as Partial<T>;
}

export async function updateVendorProfile(
  vendorId: string,
  patch: {
    businessName?: string;
    businessOwnerName?: string | null;
    description?: string;
    logoUrl?: string | null;
    brandColor?: string | null;
    mission?: string | null;
    vision?: string | null;
    valuesText?: string | null;
    displayFont?: Vendor['displayFont'] | null;
    phone?: string | null;
    website?: string | null;
    instagramUrl?: string | null;
    facebookUrl?: string | null;
    tiktokUrl?: string | null;
    linkedinUrl?: string | null;
    youtubeUrl?: string | null;
    xUrl?: string | null;
    acceptsCardPayments?: boolean;
    offersCod?: boolean;
    onlineOnly?: boolean;
    offersPickup?: boolean;
    offersDelivery?: boolean;
    pickupAddress?: string;
    pickupParish?: string;
    pickupParishes?: string[];
    pickupLatitude?: number | null;
    pickupLongitude?: number | null;
    defaultProductPickupAvailable?: boolean;
    defaultProductLocalDeliveryOnly?: boolean;
    defaultProductDeliveryPriceCents?: number | null;
    defaultProductDeliveryParishRates?: Record<string, number> | null;
    primaryColor?: string | null;
    accentColor?: string | null;
    heroTagline?: string | null;
    serviceAreas?: string[];
    bioCopy?: string | null;
    priceNote?: string | null;
    podEnabled?: boolean;
    procurementEnabled?: boolean;
    bookingNotifyChannels?: Array<'email' | 'whatsapp'>;
    businessModes?: Vendor['businessModes'];
    primaryMode?: Vendor['primaryMode'];
    directorySector?: Vendor['directorySector'];
    directoryCategoryId?: string;
    directoryCategoryCustomLabel?: string | null;
    businessType?: string;
    seoScore?: number;
  },
): Promise<Vendor | null> {
  const { phone, ...rest } = patch;
  const baseClean = omitUndefined(rest) as Record<string, unknown>;
  if (baseClean.defaultProductDeliveryParishRates !== undefined) {
    const raw = baseClean.defaultProductDeliveryParishRates;
    baseClean.defaultProductDeliveryParishRates =
      raw == null ? null : serializeDeliveryParishRates(raw as Record<string, number>) ?? null;
  }
  try {
    const client = await getAuthedDataClient();

    const tryUpdate = async (includePhone: boolean, fields: Record<string, unknown>) =>
      client.models.Vendor.update({
        id: vendorId,
        ...fields,
        ...(includePhone && phone !== undefined ? { phone: phone ?? null } : {}),
      });

    let fields = { ...baseClean };
    let res = await tryUpdate(true, fields);

    if (res.errors?.length && phone !== undefined) {
      const msg = res.errors.map((e: { message?: string }) => e.message).join('; ');
      if (msg.toLowerCase().includes('phone') || msg.includes('VendorInput')) {
        res = await tryUpdate(false, fields);
      }
    }

    if (
      res.errors?.length &&
      isVendorDefaultsSchemaError(
        res.errors.map((e: { message?: string }) => e.message).filter(Boolean).join('; '),
      )
    ) {
      const {
        pickupLatitude: _a,
        pickupLongitude: _b,
        defaultProductPickupAvailable: _c,
        defaultProductLocalDeliveryOnly: _d,
        defaultProductDeliveryPriceCents: _e,
        defaultProductDeliveryParishRates: _f,
        ...withoutDefaults
      } = fields;
      fields = withoutDefaults;
      res = await tryUpdate(true, fields);
      if (res.errors?.length && phone !== undefined) {
        const msg = res.errors.map((e: { message?: string }) => e.message).join('; ');
        if (msg.toLowerCase().includes('phone') || msg.includes('VendorInput')) {
          res = await tryUpdate(false, fields);
        }
      }
    }

    if (!res.data) return null;
    return mapVendor(res.data as Record<string, unknown>);
  } catch (e) {
    console.error('[ShopGND] updateVendorProfile failed', e);
    return null;
  }
}

export type VendorDraft = {
  ownerUserId: string;
  businessName: string;
  businessOwnerName?: string;
  /** Preferred public URL slug from onboarding; uniquified if taken. */
  slug?: string;
  description?: string;
  logoUrl?: string;
  phone?: string;
  website?: string;
  instagramUrl?: string;
  facebookUrl?: string;
  tiktokUrl?: string;
  linkedinUrl?: string;
  youtubeUrl?: string;
  xUrl?: string;
  acceptsCardPayments?: boolean;
  offersCod?: boolean;
  onlineOnly?: boolean;
  offersPickup?: boolean;
  offersDelivery?: boolean;
  pickupAddress?: string;
  pickupParish?: string;
  pickupParishes?: string[];
  pickupLatitude?: number;
  pickupLongitude?: number;
  defaultProductPickupAvailable?: boolean;
  defaultProductLocalDeliveryOnly?: boolean;
  payoutBankName?: string;
  payoutAccountHolderName?: string;
  payoutAccountNumber?: string;
  payoutBranchCode?: string;
  payoutsEnabled?: boolean;
  approvalStatus?: Vendor['approvalStatus'];
  approvalNote?: string;
  giftCertApprovalStatus?: Vendor['giftCertApprovalStatus'];
  giftCertApprovalNote?: string;
  cardPaymentApprovalStatus?: Vendor['cardPaymentApprovalStatus'];
  cardPaymentApprovalNote?: string;
  propertyListingApprovalStatus?: Vendor['propertyListingApprovalStatus'];
  propertyListingApprovalNote?: string;
  businessType?: string;
  directorySector?: Vendor['directorySector'];
  directoryCategoryId?: string;
  directoryCategoryCustomLabel?: string;
  businessModes?: Vendor['businessModes'];
  /** When set, merged into businessModes (sell → shop, pro → hire / bookings). */
  signupGoals?: SignupBusinessGoals;
  primaryMode?: Vendor['primaryMode'];
  linkedProId?: string;
  isRegisteredBusiness?: boolean;
  businessRegistrationNumber?: string;
  taxId?: string;
  registrationDocumentKey?: string;
  subscriptionPlanId?: string;
  subscriptionTrialEndsAt?: string;
  stripeCustomerId?: string;
  seoScore?: number;
  trustedPartner?: boolean;
  partnerBusinessIds?: string[];
};

/** Create or link a Vendor with SEO slug for every service pro (signup + lazy backfill). */
export async function ensureVendorLinkedToPro(pro: ServiceProvider): Promise<Vendor> {
  const existing = await getVendorByLinkedProId(pro.id);
  if (existing) return existing;

  const owned = await getVendorForOwnerUser(pro.ownerUserId);
  if (owned) {
    if (!owned.linkedProId) {
      const linked = await updateVendorRecord(owned.id, { linkedProId: pro.id });
      if (linked) return linked;
    }
    if (owned.linkedProId === pro.id) return owned;
  }

  const businessName = proPublicName(pro).trim() || 'Local business';
  const slug = await ensureUniqueVendorSlug(businessName);
  const { sector, categoryId } = resolveDirectoryClassification({
    proCategoryIds: pro.categories,
    primaryMode: 'hire',
  });
  const businessType = DIRECTORY_CATEGORY_TO_BUSINESS_TYPE[categoryId] ?? 'trades';
  const businessModes = defaultModesForDirectoryCategory(categoryId);

  const vendor = await createVendorRecord({
    ownerUserId: pro.ownerUserId,
    businessName,
    slug,
    description: pro.bio,
    logoUrl: pro.logoUrl ?? pro.avatarUrl,
    phone: pro.phone,
    website: pro.website,
    instagramUrl: pro.instagramUrl,
    facebookUrl: pro.facebookUrl,
    tiktokUrl: pro.tiktokUrl,
    linkedinUrl: pro.linkedinUrl,
    youtubeUrl: pro.youtubeUrl,
    xUrl: pro.xUrl,
    pickupParish: pro.parish,
    businessType,
    directorySector: sector,
    directoryCategoryId: categoryId,
    businessModes,
    primaryMode: primaryModeForModes(businessModes),
    linkedProId: pro.id,
    approvalStatus: 'approved',
    offersPickup: false,
    offersDelivery: false,
    payoutsEnabled: false,
    acceptsCardPayments: false,
  });

  const legacySlug = proBusinessSlug(pro);
  if (legacySlug !== vendor.slug) {
    try {
      await recordSlugRedirect(legacySlug, vendor.slug, 'vendor');
    } catch (e) {
      console.warn('[ShopGND] recordSlugRedirect pro→vendor failed', e);
    }
  }

  return vendor;
}

export async function ensureUniqueVendorSlug(
  businessName: string,
  preferredSlug?: string,
  excludeId?: string,
): Promise<string> {
  const fromPreferred = preferredSlug?.trim() ? slugifyVendorName(preferredSlug) : '';
  const base = fromPreferred || slugifyVendorName(businessName) || 'store';
  const vendors = await listVendors();
  let candidate = base;
  let suffix = 2;
  while (vendors.some((v) => v.slug === candidate && v.id !== excludeId)) {
    candidate = `${base}-${suffix}`;
    suffix += 1;
  }
  return candidate;
}

export async function createVendorRecord(draft: VendorDraft): Promise<Vendor> {
  const slug = await ensureUniqueVendorSlug(draft.businessName, draft.slug);
  const planId = draft.subscriptionPlanId as PlanId | undefined;
  const commissionBps = planId ? commissionBpsForPlan(planId) : undefined;
  const settings = await getPlatformSettings();
  const subscriptionTrialEndsAt =
    draft.subscriptionTrialEndsAt ?? (planId ? trialEndsAtForSignup(settings) : undefined);
  const cardAllowed = planId ? vendorHasFeature({ subscriptionPlanId: planId }, 'cardPayments') : false;
  const cardApproved = draft.cardPaymentApprovalStatus === 'approved';
  const acceptsCardPayments =
    cardAllowed && cardApproved ? (draft.acceptsCardPayments ?? false) : false;
  const client = await getAuthedDataClient();
  const classification = resolveDirectoryClassification({
    directorySector: draft.directorySector,
    directoryCategoryId: draft.directoryCategoryId,
    businessType: draft.businessType,
  });
  const resolvedBusinessType =
    draft.businessType ??
    (draft.directoryCategoryId
      ? DIRECTORY_CATEGORY_TO_BUSINESS_TYPE[draft.directoryCategoryId]
      : undefined);
  const businessModes =
    draft.businessModes ??
    inferBusinessModesForSignup(
      resolvedBusinessType,
      draft.signupGoals,
      undefined,
      draft.directoryCategoryId,
    );
  const primaryMode =
    draft.primaryMode ?? primaryModeForModes(businessModes) ?? primaryModeForBusinessType(resolvedBusinessType);
  const res = await client.models.Vendor.create({
    ownerUserId: draft.ownerUserId,
    businessName: draft.businessName,
    businessOwnerName: draft.businessOwnerName?.trim() || undefined,
    slug,
    description: draft.description,
    logoUrl: draft.logoUrl,
    phone: draft.phone,
    website: draft.website,
    instagramUrl: draft.instagramUrl,
    facebookUrl: draft.facebookUrl,
    tiktokUrl: draft.tiktokUrl,
    ratingAvg: 0,
    ratingCount: 0,
    acceptsCardPayments,
    offersCod: draft.offersCod ?? true,
    onlineOnly: draft.onlineOnly ?? false,
    offersPickup: draft.onlineOnly ? false : (draft.offersPickup ?? true),
    offersDelivery: draft.offersDelivery ?? true,
    pickupAddress: draft.pickupAddress,
    pickupParish: draft.pickupParish,
    pickupParishes: draft.pickupParishes?.length ? draft.pickupParishes : undefined,
    approvalStatus: draft.approvalStatus ?? 'pending',
    giftCertApprovalStatus: draft.giftCertApprovalStatus ?? 'none',
    cardPaymentApprovalStatus: draft.cardPaymentApprovalStatus ?? 'none',
    propertyListingApprovalStatus: draft.propertyListingApprovalStatus ?? 'none',
    businessType: resolvedBusinessType,
    directorySector: draft.directorySector ?? classification.sector,
    directoryCategoryId: draft.directoryCategoryId ?? classification.categoryId,
    directoryCategoryCustomLabel: draft.directoryCategoryCustomLabel
      ? normalizeDirectoryCategoryCustomLabel(draft.directoryCategoryCustomLabel)
      : undefined,
    businessModes,
    primaryMode,
    linkedProId: draft.linkedProId,
    isRegisteredBusiness: draft.isRegisteredBusiness,
    businessRegistrationNumber: draft.businessRegistrationNumber,
    taxId: draft.taxId,
    registrationDocumentKey: draft.registrationDocumentKey,
    subscriptionPlanId: draft.subscriptionPlanId,
    subscriptionTrialEndsAt,
    stripeCustomerId: draft.stripeCustomerId,
    commissionBps,
  });
  if (res.errors?.length) {
    const raw = res.errors.map((e: { message?: string }) => e.message).filter(Boolean).join('; ');
    if (raw.toLowerCase().includes('not authorized') || raw.toLowerCase().includes('unauthorized')) {
      throw new Error(
        'Your account cannot create a store yet. If this continues after a fresh sign-in, the backend may need to be redeployed.',
      );
    }
    throw new Error(raw || 'Could not create vendor');
  }
  if (!res.data) throw new Error('Could not create vendor');
  const vendor = mapVendor(res.data as Record<string, unknown>);
  try {
    const { createOrgForVendor, invalidateWorkspaceQuery } = await import('@/api/orgs');
    await createOrgForVendor(vendor);
    invalidateWorkspaceQuery(vendor.ownerUserId);
  } catch (e) {
    console.warn('[ShopGND] createOrgForVendor failed', e);
  }
  try {
    await assignHubRole('vendor');
    await refreshAuthUser({ forceRefresh: true });
  } catch (e) {
    console.warn('[ShopGND] assignHubRole(vendor) failed', e);
  }
  return vendor;
}

export async function updateVendorRecord(
  vendorId: string,
  patch: Partial<VendorDraft>,
): Promise<Vendor | null> {
  const { slug: slugPatch, ...restPatch } = patch;
  let previousSlug: string | undefined;
  if (slugPatch !== undefined) {
    const existing = await getVendor(vendorId);
    previousSlug = existing?.slug?.trim().toLowerCase();
  }
  const client = await getAuthedDataClient();
  const res = await client.models.Vendor.update({ id: vendorId, ...restPatch, ...(slugPatch !== undefined ? { slug: slugPatch } : {}) });
  if (res.errors?.length) {
    const raw = res.errors.map((e: { message?: string }) => e.message).filter(Boolean).join('; ');
    if (
      raw.includes("field that is not defined for input object type 'UpdateVendorInput'") ||
      raw.includes('UpdateVendorInput')
    ) {
      throw new Error('Vendor approval fields require backend deploy before updates can be saved.');
    }
    throw new Error(raw || 'Could not update vendor record');
  }
  if (!res.data) return null;
  const vendor = mapVendor(res.data as Record<string, unknown>);
  if (
    slugPatch !== undefined &&
    previousSlug &&
    vendor.slug &&
    previousSlug !== vendor.slug.trim().toLowerCase()
  ) {
    await recordSlugRedirect(previousSlug, vendor.slug, 'vendor');
  }
  return vendor;
}

/** Admin only — set verified status on a vendor. */
export async function setVendorVerified(
  vendorId: string,
  verified: boolean,
  adminUserId: string,
): Promise<void> {
  const client = await getAuthedDataClient();
  await client.models.Vendor.update({
    id: vendorId,
    verified,
    verifiedAt: verified ? new Date().toISOString() : undefined,
    verifiedByAdminId: verified ? adminUserId : undefined,
  });
}

export async function deleteVendorRecord(
  vendorId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const client = await getAuthedDataClient();
    const res = await client.models.Vendor.delete({ id: vendorId });
    const messages = res.errors
      ?.map((e: { message?: string }) => e.message)
      .filter(Boolean)
      .join('; ');
    if (messages) return { ok: false, error: messages };
    notifyCatalogRefresh();
    return { ok: true };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Could not delete vendor';
    console.error('[ShopGND] deleteVendorRecord failed', e);
    return { ok: false, error: message };
  }
}

export type ProductDraft = {
  title: string;
  slug?: string;
  description?: string;
  priceCents: number;
  currency: string;
  categoryId: string;
  categoryIds?: string[];
  images: string[];
  stock: number;
  parish?: string;
  sponsoredRank?: number;
  variants?: ProductVariant[];
  variantMultiSelect?: boolean;
  variantConfig?: ProductVariantConfig | null;
  pairsWith?: string[];
  /** When set, creates a category request and saves product as pending_category. */
  newCategoryName?: string;
  listingStatus?: Product['listingStatus'];
  categoryRequestId?: string;
  listingNote?: string;
  condition?: Product['condition'];
  isLocallyMade?: boolean;
  seoTitle?: string;
  seoDescription?: string;
  searchKeywords?: string[];
  lowStockThreshold?: number;
  allowBackorders?: boolean;
  scheduledPublishAt?: string;
  weightKg?: number;
  shippingClass?: Product['shippingClass'];
  pickupAvailable?: boolean;
  pickupLocation?: string;
  pickupLatitude?: number;
  pickupLongitude?: number;
  localDeliveryOnly?: boolean;
  deliveryPriceCents?: number;
  deliveryParishRates?: Record<string, number>;
  shortDescription?: string;
  compareAtCents?: number;
  costCents?: number;
  sku?: string;
  barcode?: string;
  taxable?: boolean;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  imageAlts?: string[];
  altTexts?: string[];
  seoStatus?: Product['seoStatus'];
  featured?: boolean;
  soldCount?: number;
};

function productDraftPayload(draft: Partial<ProductDraft>): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (draft.title !== undefined) payload.title = draft.title;
  if (draft.slug !== undefined) payload.slug = draft.slug;
  if (draft.description !== undefined) payload.description = draft.description;
  if (draft.priceCents !== undefined) payload.priceCents = draft.priceCents;
  if (draft.currency !== undefined) payload.currency = draft.currency;
  if (draft.categoryId !== undefined || draft.categoryIds !== undefined) {
    const ids = normalizeProductCategoryIds(draft.categoryIds ?? draft.categoryId);
    if (ids.length > 0) {
      payload.categoryId = ids[0];
      payload.categoryIds = ids;
    } else if (draft.categoryId !== undefined) {
      payload.categoryId = draft.categoryId;
    }
  }
  if (draft.images !== undefined) payload.images = normalizeImageList(draft.images);
  if (draft.stock !== undefined) payload.stock = draft.stock;
  if (draft.parish !== undefined) payload.parish = draft.parish;
  if (draft.sponsoredRank !== undefined) payload.sponsoredRank = draft.sponsoredRank;
  if (draft.variants !== undefined) payload.variants = serializeVariants(draft.variants);
  if (draft.variantMultiSelect !== undefined) payload.variantMultiSelect = draft.variantMultiSelect;
  if (draft.variantConfig !== undefined) {
    payload.variantConfig = draft.variantConfig ?? null;
  }
  if (draft.listingStatus !== undefined) payload.listingStatus = draft.listingStatus;
  if (draft.categoryRequestId !== undefined) payload.categoryRequestId = draft.categoryRequestId;
  if (draft.listingNote !== undefined) payload.listingNote = draft.listingNote;
  if (draft.condition !== undefined) payload.condition = draft.condition;
  if (draft.isLocallyMade !== undefined) payload.isLocallyMade = draft.isLocallyMade;
  if (draft.seoTitle !== undefined) payload.seoTitle = draft.seoTitle;
  if (draft.seoDescription !== undefined) payload.seoDescription = draft.seoDescription;
  if (draft.searchKeywords !== undefined) payload.searchKeywords = draft.searchKeywords;
  if (draft.lowStockThreshold !== undefined) payload.lowStockThreshold = draft.lowStockThreshold;
  if (draft.allowBackorders !== undefined) payload.allowBackorders = draft.allowBackorders;
  if (draft.scheduledPublishAt !== undefined) payload.scheduledPublishAt = draft.scheduledPublishAt;
  if (draft.weightKg !== undefined) payload.weightKg = draft.weightKg;
  if (draft.shippingClass !== undefined) payload.shippingClass = draft.shippingClass;
  if (draft.pickupAvailable !== undefined) payload.pickupAvailable = draft.pickupAvailable;
  if (draft.pickupLocation !== undefined) payload.pickupLocation = draft.pickupLocation;
  if (draft.pickupLatitude !== undefined) payload.pickupLatitude = draft.pickupLatitude;
  if (draft.pickupLongitude !== undefined) payload.pickupLongitude = draft.pickupLongitude;
  if (draft.localDeliveryOnly !== undefined) payload.localDeliveryOnly = draft.localDeliveryOnly;
  if (draft.deliveryPriceCents !== undefined) payload.deliveryPriceCents = draft.deliveryPriceCents;
  if (draft.deliveryParishRates !== undefined) {
    const serialized = serializeDeliveryParishRates(draft.deliveryParishRates);
    payload.deliveryParishRates = serialized ?? null;
  }
  if (draft.shortDescription !== undefined) payload.shortDescription = draft.shortDescription;
  if (draft.compareAtCents !== undefined) payload.compareAtCents = draft.compareAtCents;
  if (draft.costCents !== undefined) payload.costCents = draft.costCents;
  if (draft.sku !== undefined) payload.sku = draft.sku;
  if (draft.barcode !== undefined) payload.barcode = draft.barcode;
  if (draft.taxable !== undefined) payload.taxable = draft.taxable;
  if (draft.lengthCm !== undefined) payload.lengthCm = draft.lengthCm;
  if (draft.widthCm !== undefined) payload.widthCm = draft.widthCm;
  if (draft.heightCm !== undefined) payload.heightCm = draft.heightCm;
  if (draft.imageAlts !== undefined) payload.imageAlts = draft.imageAlts;
  if (draft.altTexts !== undefined) payload.altTexts = draft.altTexts;
  if (draft.seoStatus !== undefined) payload.seoStatus = draft.seoStatus;
  if (draft.featured !== undefined) payload.featured = draft.featured;
  if (draft.soldCount !== undefined) payload.soldCount = draft.soldCount;
  return payload;
}

export async function createProduct(
  vendorId: string,
  draft: ProductDraft,
  ownerUserId?: string,
): Promise<Product> {
  const client = await getAuthedDataClient();
  let owner = ownerUserId;
  if (!owner) {
    const vendorRes = await client.models.Vendor.get({ id: vendorId });
    owner = vendorRes.data?.ownerUserId as string | undefined;
  }
  if (!owner) throw new Error('Vendor owner not found');

  const vendor = await getVendor(vendorId);
  if (!vendor) throw new Error('Vendor not found');
  const existingProducts = await listProductsByVendor(vendorId);
  const isAdmin = await callerIsShopAdmin();
  if (!isAdmin && vendor.approvalStatus !== 'approved') {
    throw new Error('Your store must be approved before you can list products.');
  }
  if (!isAdmin && vendorAtListingLimit(vendor, existingProducts.length)) {
    const max = vendorListingLimit(vendor);
    throw new Error(
      max === null
        ? 'Could not create product.'
        : `You have reached your product listing limit (${max}). Upgrade your plan for more listings.`,
    );
  }

  const normalizedCategories = normalizeProductCategoryIds(draft.categoryIds ?? draft.categoryId);
  let categoryId = normalizedCategories[0] ?? draft.categoryId;
  let categoryIds = normalizedCategories;
  let listingStatus = draft.listingStatus ?? 'published';
  let categoryRequestId = draft.categoryRequestId;
  let listingNote = draft.listingNote;

  if (draft.newCategoryName?.trim()) {
    const vendor = await getVendor(vendorId);
    if (!vendor) throw new Error('Vendor not found');
    const req = await createCategoryRequest(vendor, owner, draft.newCategoryName.trim());
    categoryId = PENDING_CATEGORY_ID;
    categoryIds = [PENDING_CATEGORY_ID];
    listingStatus = 'pending_category';
    categoryRequestId = req.id;
    listingNote = undefined;
  }

  const slug = draft.slug?.trim()
    ? draft.slug.trim().toLowerCase()
    : await allocateProductSlug(draft.title, draft.parish);

  const draftForPayload = {
    ...draft,
    categoryId,
    categoryIds,
    listingStatus,
    categoryRequestId,
    listingNote,
  };

  const mustUseListingLambda = !isAdmin;
  if (mustUseListingLambda) {
    try {
      const { hubCreateProduct, isHubApiConfigured } = await import('@/api/hub');
      const created =
        (await isHubApiConfigured())
          ? await hubCreateProduct(vendorId, {
              ...draft,
              categoryId,
              categoryIds,
              listingStatus,
              categoryRequestId,
              listingNote,
              slug,
            })
          : await createProductViaLambda(vendorId, {
              ...draft,
              categoryId,
              categoryIds,
              listingStatus,
              categoryRequestId,
              listingNote,
              slug,
            });
      const saved = await getProduct(created.id);
      if (!saved) {
        throw new Error('Product was saved but could not be loaded. Refresh the products list.');
      }
      return saved;
    } catch (e) {
      if (isProductListingLambdaUnreachableError(e)) {
        throw new Error(
          'Product listing service is temporarily unavailable. Please try again in a few minutes.',
        );
      }
      throw e;
    }
  }

  // Admin-only AppSync create path (group authorization).
  const basePayload: Record<string, unknown> = {
    vendorId,
    ownerUserId: owner,
    title: draft.title,
    slug,
    ...productDraftPayload(draftForPayload),
  };

  const createWithPayload = (payload: Record<string, unknown>, withPairsWith: boolean) =>
    client.models.Product.create({
      ...payload,
      ...(withPairsWith && draft.pairsWith?.length ? { pairsWith: draft.pairsWith } : {}),
    });

  let res = await createWithPayload(basePayload, true);

  if (res.errors?.length) {
    const raw = joinGraphqlErrors(res.errors);
    if (draft.pairsWith?.length && isPairsWithSchemaError(raw)) {
      res = await createWithPayload(basePayload, false);
    } else if (isCategoryIdsSchemaError(raw)) {
      const { categoryIds: _omit, ...withoutCategoryIds } = basePayload;
      res = await createWithPayload(withoutCategoryIds, Boolean(draft.pairsWith?.length));
      if (res.errors?.length && draft.pairsWith?.length) {
        const raw2 = joinGraphqlErrors(res.errors);
        if (isPairsWithSchemaError(raw2)) {
          res = await createWithPayload(withoutCategoryIds, false);
        }
      }
    }
  }

  if (res.errors?.length && isProductInputSchemaError(joinGraphqlErrors(res.errors))) {
    res = await retryProductMutationWithoutUnknownFields(res, basePayload, (payload) =>
      createWithPayload(payload, Boolean(draft.pairsWith?.length)),
    );
    if (res.errors?.length && draft.pairsWith?.length && isPairsWithSchemaError(joinGraphqlErrors(res.errors))) {
      res = await retryProductMutationWithoutUnknownFields(res, basePayload, (payload) =>
        createWithPayload(payload, false),
      );
    }
  }

  if (res.errors?.length) {
    throw new Error(mapVariantSchemaError(joinGraphqlErrors(res.errors)));
  }
  if (!res.data) throw new Error('Could not create product');
  return mapProduct(res.data as Record<string, unknown>);
}

export async function updateProduct(
  productId: string,
  patch: Partial<ProductDraft>,
): Promise<Product> {
  const isAdmin = await callerIsShopAdmin();
  const existing = await getProduct(productId);
  if (!existing) throw new Error('Product not found');

  if (!isAdmin) {
    try {
      const { hubUpdateProduct, isHubApiConfigured } = await import('@/api/hub');
      if (await isHubApiConfigured()) {
        await hubUpdateProduct(productId, existing.vendorId, patch);
      } else {
        await updateProductViaLambda(productId, existing.vendorId, patch);
      }
      const refreshed = await getProduct(productId);
      if (!refreshed) throw new Error('Could not load updated product');
      if (
        patch.slug !== undefined &&
        existing.slug &&
        refreshed.slug &&
        existing.slug.trim().toLowerCase() !== refreshed.slug.trim().toLowerCase()
      ) {
        await recordSlugRedirect(existing.slug, refreshed.slug, 'product');
      }
      return refreshed;
    } catch (e) {
      if (isProductListingLambdaUnreachableError(e)) {
        throw new Error(
          'Product listing service is temporarily unavailable. Please try again in a few minutes.',
        );
      }
      throw e;
    }
  }

  const client = await getAuthedDataClient();
  const { pairsWith, slug, ...rest } = patch;

  let previousSlug: string | undefined;
  if (slug !== undefined) {
    const prior = await getProduct(productId);
    previousSlug = prior?.slug?.trim().toLowerCase();
  }

  let resolvedSlug = slug;
  if (resolvedSlug !== undefined) {
    resolvedSlug = resolvedSlug.trim().toLowerCase();
    const existing = await listProductsBySlug(resolvedSlug);
    if (existing && existing.id !== productId) {
      resolvedSlug = await allocateProductSlug(patch.title ?? existing.title, productId);
    }
  }

  const buildPayload = (withPairsWith: boolean) => ({
    id: productId,
    ...productDraftPayload(rest),
    ...(resolvedSlug !== undefined ? { slug: resolvedSlug } : {}),
    ...(withPairsWith && pairsWith !== undefined
      ? { pairsWith: pairsWith.length ? pairsWith : null }
      : {}),
  });

  const mergeUpdatePayload = (draftPayload: Record<string, unknown>, withPairsWith: boolean) => ({
    id: productId,
    ...draftPayload,
    ...(resolvedSlug !== undefined ? { slug: resolvedSlug } : {}),
    ...(withPairsWith && pairsWith !== undefined
      ? { pairsWith: pairsWith.length ? pairsWith : null }
      : {}),
  });

  let res = await client.models.Product.update(buildPayload(true));

  if (res.errors?.length) {
    const raw = joinGraphqlErrors(res.errors);
    if (pairsWith !== undefined && isPairsWithSchemaError(raw)) {
      res = await client.models.Product.update(buildPayload(false));
    } else if (isCategoryIdsSchemaError(raw)) {
      const withoutCategoryIds = productDraftPayload({ ...rest, categoryIds: undefined });
      res = await client.models.Product.update(mergeUpdatePayload(withoutCategoryIds, true));
      if (res.errors?.length && pairsWith !== undefined && isPairsWithSchemaError(joinGraphqlErrors(res.errors))) {
        res = await client.models.Product.update(mergeUpdatePayload(withoutCategoryIds, false));
      }
    }
  }

  if (res.errors?.length && isProductInputSchemaError(joinGraphqlErrors(res.errors))) {
    const base = productDraftPayload(rest);
    res = await retryProductMutationWithoutUnknownFields(res, base, (draftPayload) =>
      client.models.Product.update(mergeUpdatePayload(draftPayload, true)),
    );
    if (res.errors?.length && pairsWith !== undefined && isPairsWithSchemaError(joinGraphqlErrors(res.errors))) {
      res = await retryProductMutationWithoutUnknownFields(res, base, (draftPayload) =>
        client.models.Product.update(mergeUpdatePayload(draftPayload, false)),
      );
    }
  }

  if (res.errors?.length) {
    throw new Error(mapVariantSchemaError(joinGraphqlErrors(res.errors)));
  }
  if (!res.data) throw new Error('Could not update product');
  const product = mapProduct(res.data as Record<string, unknown>);
  if (
    resolvedSlug !== undefined &&
    previousSlug &&
    product.slug &&
    previousSlug !== product.slug.trim().toLowerCase()
  ) {
    await recordSlugRedirect(previousSlug, product.slug, 'product');
  }
  return product;
}

/** Sponsored and highly rated picks for shop rails (excludes current result set). */
export async function listRecommendedProducts(
  excludeIds: string[] = [],
  limit = 8,
): Promise<Product[]> {
  const exclude = new Set(excludeIds);
  try {
    const client = await getCatalogClient();
    const items = await listProductRecords(client, { stock: { gt: 0 } });
    const list = items
      .map(mapProduct)
      .filter((p) => !exclude.has(p.id) && isPublishedProduct(p))
      .sort((a, b) => (b.sponsoredRank ?? 0) - (a.sponsoredRank ?? 0));
    return list.slice(0, limit);
  } catch (e) {
    console.error('[ShopGND] listRecommendedProducts failed', e);
    return [];
  }
}

export async function decrementProductStock(
  productId: string,
  quantity: number,
): Promise<boolean> {
  try {
    const client = await getAuthedDataClient();
    const res = await client.models.Product.get({ id: productId });
    const product = res.data as { stock?: number } | null | undefined;
    if (!product || (product.stock ?? 0) < quantity) return false;
    const updated = await client.models.Product.update({
      id: productId,
      stock: (product.stock ?? 0) - quantity,
    });
    if (updated.errors?.length || !updated.data) return false;
    return true;
  } catch {
    return false;
  }
}

export type StockValidationResult = {
  valid: boolean;
  issues: { productId: string; title: string; requested: number; available: number }[];
};

export async function validateCartStock(
  lines: { productId: string; quantity: number; title: string; variantId?: string }[],
): Promise<StockValidationResult> {
  const issues: StockValidationResult['issues'] = [];
  for (const line of lines) {
    const product = await getProduct(line.productId);
    if (!product) {
      issues.push({ productId: line.productId, title: line.title, requested: line.quantity, available: 0 });
      continue;
    }
    if (!isPurchasableProduct(product)) {
      issues.push({ productId: line.productId, title: line.title, requested: line.quantity, available: 0 });
      continue;
    }
    if (line.variantId && product.variants?.length) {
      const variant = product.variants.find((v) => v.id === line.variantId);
      if (!variant) {
        issues.push({ productId: line.productId, title: line.title, requested: line.quantity, available: 0 });
        continue;
      }
    } else if (product.variants?.length) {
      issues.push({ productId: line.productId, title: line.title, requested: line.quantity, available: 0 });
      continue;
    }
    let available: number;
    if (line.variantId && product.variants?.length) {
      const variant = product.variants.find((v) => v.id === line.variantId);
      available = variant?.stock ?? 0;
    } else {
      available = product.stock;
    }
    if (product.allowBackorders && available <= 0) continue;
    if (line.quantity > available) {
      issues.push({ productId: line.productId, title: line.title, requested: line.quantity, available });
    }
  }
  return { valid: issues.length === 0, issues };
}

export type ProductQuestionsPage = {
  items: ProductQuestion[];
  nextToken?: string;
};

export async function getProductQuestions(
  productId: string,
  limit = 5,
  nextToken?: string,
): Promise<ProductQuestionsPage> {
  try {
    const client = await getCatalogClient();
    const res = await client.models.ProductQuestion.list({
      filter: { productId: { eq: productId } },
      limit,
      nextToken,
    });
    const items = ((res.data ?? []) as Record<string, unknown>[])
      .map(mapProductQuestion)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return { items, nextToken: res.nextToken ?? undefined };
  } catch (e) {
    console.error('[ShopGND] getProductQuestions failed', e);
    return { items: [] };
  }
}

export async function createProductQuestion(
  productId: string,
  question: string,
  asker: { userId: string; displayName: string },
): Promise<ProductQuestion | null> {
  try {
    const product = await getProduct(productId);
    if (!product) throw new Error('Product not found');
    const client = await getAuthedDataClient();
    const res = await client.models.ProductQuestion.create({
      productId,
      productOwnerUserId: (await getVendor(product.vendorId))?.ownerUserId ?? product.vendorId,
      userId: asker.userId,
      userDisplayName: asker.displayName,
      question: question.trim(),
    });
    if (!res.data) return null;
    return mapProductQuestion(res.data as Record<string, unknown>);
  } catch (e) {
    console.error('[ShopGND] createProductQuestion failed', e);
    return null;
  }
}

export async function answerProductQuestion(
  questionId: string,
  answer: string,
  actor: { userId: string; isAdmin: boolean },
  productOwnerUserId?: string,
): Promise<ProductQuestion | null> {
  if (!actor.isAdmin && actor.userId !== productOwnerUserId) {
    throw new Error('Not authorized to answer this question');
  }
  try {
    const client = await getAuthedDataClient();
    const res = await client.models.ProductQuestion.update({
      id: questionId,
      answer: answer.trim(),
      answeredAt: nowIso(),
      answeredByUserId: actor.userId,
    });
    if (!res.data) return null;
    return mapProductQuestion(res.data as Record<string, unknown>);
  } catch (e) {
    console.error('[ShopGND] answerProductQuestion failed', e);
    return null;
  }
}

export async function getClassified(id: string): Promise<Classified | undefined> {
  try {
    const client = await getCatalogClient();
    const res = await client.models.Classified.get({ id });
    return res.data ? mapClassified(res.data as Record<string, unknown>) : undefined;
  } catch (e) {
    console.error('[ShopGND] getClassified failed', e);
    return undefined;
  }
}

export async function getClassifiedBySlug(slug: string): Promise<Classified | undefined> {
  const normalized = slug.trim().toLowerCase();
  if (!normalized) return undefined;
  try {
    const client = await getCatalogClient();
    const res = await client.models.Classified.list({
      filter: { slug: { eq: normalized } },
      limit: 1,
    });
    const row = (res.data ?? [])[0] as Record<string, unknown> | undefined;
    return row ? mapClassified(row) : undefined;
  } catch (e) {
    console.error('[ShopGND] getClassifiedBySlug failed', e);
    return undefined;
  }
}

export async function getClassifiedBySlugOrId(slugOrId: string): Promise<Classified | undefined> {
  const byId = await getClassified(slugOrId);
  if (byId) return byId;
  return getClassifiedBySlug(slugOrId);
}

export async function getPropertyListing(slugOrId: string): Promise<Classified | undefined> {
  const byId = await getClassified(slugOrId);
  if (byId) return byId;
  const all = await listClassifieds({ category: 'property' });
  return all.find((c) => c.slug === slugOrId);
}

export async function listClassifiedsByVendor(vendorId: string): Promise<Classified[]> {
  try {
    const client = await getCatalogClient();
    const res = await client.models.Classified.list({
      filter: { vendorId: { eq: vendorId }, category: { eq: 'property' } },
    });
    return ((res.data ?? []) as Record<string, unknown>[]).map(mapClassified);
  } catch (e) {
    console.error('[ShopGND] listClassifiedsByVendor failed', e);
    return [];
  }
}

export function filterRealEstateAgencies(vendors: Vendor[]): Vendor[] {
  return vendors.filter(
    (v) =>
      v.approvalStatus === 'approved' &&
      vendorHasPropertyListingAccess(v) &&
      ((v.businessType ?? '').toLowerCase() === 'real_estate' ||
        v.businessModes?.includes('listings')),
  );
}

export async function listRealEstateVendors(): Promise<Vendor[]> {
  return filterRealEstateAgencies(await listVendors());
}

export async function listClassifiedsBySeller(sellerUserId: string): Promise<Classified[]> {
  try {
    const client = await getAuthedDataClient();
    const res = await client.models.Classified.list({
      filter: { sellerUserId: { eq: sellerUserId } },
    });
    return ((res.data ?? []) as Record<string, unknown>[]).map(mapClassified);
  } catch (e) {
    console.error('[ShopGND] listClassifiedsBySeller failed', e);
    return [];
  }
}

export async function listAllClassifieds(): Promise<Classified[]> {
  try {
    const client = await getAuthedDataClient();
    const res = await client.models.Classified.list();
    return ((res.data ?? []) as Record<string, unknown>[]).map(mapClassified);
  } catch (e) {
    console.error('[ShopGND] listAllClassifieds failed', e);
    return [];
  }
}

export async function updateClassifiedStatus(
  id: string,
  status: ClassifiedStatus,
  patch?: { reportCount?: number },
): Promise<Classified | null> {
  try {
    const client = await getAuthedDataClient();
    const res = await client.models.Classified.update({
      id,
      status,
      ...(patch?.reportCount !== undefined ? { reportCount: patch.reportCount } : {}),
    });
    if (!res.data) return null;
    return mapClassified(res.data as Record<string, unknown>);
  } catch (e) {
    console.error('[ShopGND] updateClassifiedStatus failed', e);
    return null;
  }
}

export type ClassifiedDraft = {
  title: string;
  description?: string;
  priceCents: number;
  currency: string;
  images: string[];
  parish: string;
  category?: Classified['category'];
  listingIntent?: Classified['listingIntent'];
  propertyType?: Classified['propertyType'];
  bedrooms?: number;
  bathrooms?: number;
  sellerType?: Classified['sellerType'];
  vendorId?: string;
  slug?: string;
  areaLabel?: string;
  latitude?: number;
  longitude?: number;
  sqftInterior?: number;
  sqftLot?: number;
  furnished?: Classified['furnished'];
  amenities?: string[];
  yearBuilt?: number;
  parkingSpaces?: number;
  virtualTourUrl?: string;
  contactPolicy?: Classified['contactPolicy'];
  contactPhone?: string;
  contactEmail?: string;
  listingAgentName?: string;
  imageAlts?: string[];
  seoTitle?: string;
  seoDescription?: string;
  seoStatus?: Classified['seoStatus'];
};

export type PropertyListingDraft = ClassifiedDraft & {
  listingIntent: NonNullable<Classified['listingIntent']>;
  propertyType: NonNullable<Classified['propertyType']>;
};

function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  ) as Partial<T>;
}

function legacyPropertyFields(draft: ClassifiedDraft): Record<string, unknown> {
  return stripUndefined({
    listingIntent: draft.listingIntent,
    propertyType: draft.propertyType,
    bedrooms: draft.bedrooms,
    bathrooms: draft.bathrooms,
  });
}

function extendedPropertyFields(draft: ClassifiedDraft): Record<string, unknown> {
  return stripUndefined({
    ...legacyPropertyFields(draft),
    slug:
      draft.slug ??
      `${draft.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 50)}-${crypto.randomUUID().slice(0, 8)}`,
    areaLabel: draft.areaLabel,
    latitude: draft.latitude,
    longitude: draft.longitude,
    sqftInterior: draft.sqftInterior,
    sqftLot: draft.sqftLot,
    furnished: draft.furnished,
    amenities: draft.amenities?.length ? draft.amenities : undefined,
    yearBuilt: draft.yearBuilt,
    parkingSpaces: draft.parkingSpaces,
    virtualTourUrl: draft.virtualTourUrl,
    contactPolicy: draft.contactPolicy ?? 'chat_only',
    contactPhone: draft.contactPhone,
    contactEmail: draft.contactEmail,
    listingAgentName: draft.listingAgentName,
    imageAlts: draft.imageAlts?.length ? draft.imageAlts : undefined,
    seoTitle: draft.seoTitle,
    seoDescription: draft.seoDescription,
    seoStatus: draft.seoStatus,
  });
}

function classifiedPropertyPayload(draft: ClassifiedDraft, extended: boolean) {
  return extended ? extendedPropertyFields(draft) : legacyPropertyFields(draft);
}

function amplifyErrorMessage(errors: { message: string }[] | undefined, fallback: string): string {
  const msg = errors?.map((e) => e.message).filter(Boolean).join('; ');
  return msg || fallback;
}

export async function updateClassified(
  id: string,
  patch: Partial<ClassifiedDraft> & { status?: ClassifiedStatus },
): Promise<Classified | null> {
  try {
    const client = await getAuthedDataClient();
    const { images, imageAlts, ...rest } = patch;
    const res = await client.models.Classified.update(
      stripUndefined({
        id,
        ...rest,
        ...(images ? { images: normalizeImageList(images) } : {}),
        ...(imageAlts !== undefined
          ? { imageAlts: imageAlts.length ? imageAlts : undefined }
          : {}),
      }),
    );
    if (!res.data) return null;
    return mapClassified(res.data as Record<string, unknown>);
  } catch (e) {
    console.error('[ShopGND] updateClassified failed', e);
    return null;
  }
}

export async function createClassified(
  sellerUserId: string,
  draft: ClassifiedDraft,
): Promise<Classified> {
  const vendors = await listVendors();
  const ownedStore = vendors.find((v) => v.ownerUserId === sellerUserId);
  if (ownedStore?.approvalStatus === 'approved') {
    if (isRealEstateVendor(ownedStore) && draft.category === 'property') {
      throw new Error('Use your vendor dashboard to publish agency property listings.');
    }
  }

  const category = draft.category ?? 'general';
  const sellerType = draft.sellerType ?? 'individual';
  const { classifiedHasExtendedPropertyFields } = await import('@/lib/classifiedSchemaCapabilities');
  const extendedSchema = category === 'property' ? await classifiedHasExtendedPropertyFields() : false;
  const propertyExtras =
    category === 'property' ? classifiedPropertyPayload(draft, extendedSchema) : {};
  const classifiedSlug =
    category !== 'property'
      ? { slug: draft.slug ?? buildListingSlug(draft.title, draft.parish, 'grenada') }
      : {};

  const client = await getAuthedDataClient();
  const input = stripUndefined({
    sellerUserId,
    title: draft.title,
    description: draft.description,
    priceCents: draft.priceCents,
    currency: draft.currency,
    images: normalizeImageList(draft.images),
    ...(draft.imageAlts?.length ? { imageAlts: draft.imageAlts } : {}),
    parish: draft.parish,
    status: 'active',
    reportCount: 0,
    category,
    sellerType,
    ...classifiedSlug,
    ...propertyExtras,
  });

  try {
    const res = await client.models.Classified.create(input);
    if (!res.data) {
      throw new Error(
        amplifyErrorMessage(res.errors as { message: string }[] | undefined, 'Could not create classified'),
      );
    }
    return mapClassified(res.data as Record<string, unknown>);
  } catch (e) {
    if (e instanceof Error) throw e;
    console.error('[ShopGND] createClassified failed', e);
    throw new Error('Could not create classified');
  }
}

function mapListingReport(row: Record<string, unknown>): ListingReport {
  return {
    id: String(row.id),
    targetType: String(row.targetType) as ListingReport['targetType'],
    targetId: String(row.targetId),
    targetTitle: String(row.targetTitle),
    reporterUserId: row.reporterUserId ? String(row.reporterUserId) : undefined,
    reason: String(row.reason),
    details: row.details ? String(row.details) : undefined,
    status: (row.status ? String(row.status) : 'open') as ListingReport['status'],
    createdAt: String(row.createdAt ?? nowIso()),
    updatedAt: String(row.updatedAt ?? nowIso()),
  };
}

/** Buyer-submitted report — stored for admin moderation queue. */
export async function submitListingReport(input: {
  targetType: ListingReport['targetType'];
  targetId: string;
  targetTitle: string;
  reason: string;
  details?: string;
  reporterUserId?: string;
}): Promise<boolean> {
  try {
    const client = await getAuthedDataClient();
    const res = await client.models.ListingReport.create({
      targetType: input.targetType,
      targetId: input.targetId,
      targetTitle: input.targetTitle,
      reporterUserId: input.reporterUserId,
      reason: input.reason,
      details: input.details,
      status: 'open',
    });
    return !!res.data;
  } catch (e) {
    console.error('[ShopGND] submitListingReport failed', e);
    return false;
  }
}

export async function listOpenListingReports(): Promise<ListingReport[]> {
  try {
    const client = await getAuthedDataClient();
    const rows: ListingReport[] = [];
    let nextToken: string | undefined;
    do {
      const res = await client.models.ListingReport.list({
        filter: { status: { eq: 'open' } },
        nextToken,
      });
      for (const row of res.data ?? []) {
        rows.push(mapListingReport(row as Record<string, unknown>));
      }
      nextToken = res.nextToken ?? undefined;
    } while (nextToken);
    return rows.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  } catch (e) {
    console.error('[ShopGND] listOpenListingReports failed', e);
    return [];
  }
}

export async function updateListingReportStatus(
  id: string,
  status: ListingReport['status'],
): Promise<boolean> {
  try {
    const client = await getAuthedDataClient();
    const res = await client.models.ListingReport.update({ id, status });
    return !!res.data;
  } catch (e) {
    console.error('[ShopGND] updateListingReportStatus failed', e);
    return false;
  }
}
